/**
 * @fileoverview GPS location capture utility for inspection forms.
 * @module inspection-renderer/utils
 *
 * Responsibilities:
 * - Capture a single GPS fix using navigator.geolocation.getCurrentPosition
 * - Return lat/lng/accuracy in the canonical LocationValue shape
 * - Handle permission errors and timeout scenarios
 *
 * Dependencies:
 * - Browser Geolocation API
 *
 * Architecture:
 * - Pure client-side utility; no Dexie or API calls
 *
 * Notes:
 * - Uses high accuracy mode by default for inspection use cases
 * - 30-second timeout to match mobile-pwa's useLocationWatch behavior
 */

import type { LocationValue } from '../fields/LocationField';

export interface CaptureLocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

export interface CaptureLocationError {
  code: 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'NOT_SUPPORTED';
  message: string;
}

/**
 * @process captureCurrentLocation
 * Purpose: Capture a single GPS fix from the device.
 * Steps:
 *   1. Check if geolocation API is available
 *   2. Request current position with high accuracy
 *   3. Return canonical LocationValue on success
 * Inputs: Optional configuration (accuracy, timeout)
 * Outputs: Promise<LocationValue> or throws CaptureLocationError
 * Failure Handling: Rejects with structured error containing code and message
 * Idempotency: NOT REQUIRED (each call captures a new GPS fix)
 */
export async function captureCurrentLocation(
  options: CaptureLocationOptions = {}
): Promise<LocationValue> {
  const {
    enableHighAccuracy = true,
    timeout = 30_000,
    maximumAge = 0,
  } = options;

  if (!navigator.geolocation) {
    const error: CaptureLocationError = {
      code: 'NOT_SUPPORTED',
      message: 'Geolocation is not supported by this browser',
    };
    throw error;
  }

  return new Promise<LocationValue>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location: LocationValue = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracyM: position.coords.accuracy,
        };
        resolve(location);
      },
      (err) => {
        let code: CaptureLocationError['code'];
        switch (err.code) {
          case GeolocationPositionError.PERMISSION_DENIED:
            code = 'PERMISSION_DENIED';
            break;
          case GeolocationPositionError.POSITION_UNAVAILABLE:
            code = 'POSITION_UNAVAILABLE';
            break;
          case GeolocationPositionError.TIMEOUT:
            code = 'TIMEOUT';
            break;
          default:
            code = 'POSITION_UNAVAILABLE';
        }

        const error: CaptureLocationError = {
          code,
          message: err.message || 'Failed to capture location',
        };
        reject(error);
      },
      {
        enableHighAccuracy,
        timeout,
        maximumAge,
      }
    );
  });
}

/**
 * @contract ReverseGeocoder
 * Purpose: Host-injected callback to convert lat/lng to a human-readable address.
 * Inputs: lat, lng coordinates
 * Outputs: Promise<string> with formatted address or null on failure
 * Notes: Renderer does not implement geocoding directly — host must inject this
 *        capability via context if reverse geocoding is required.
 */
export type ReverseGeocoder = (lat: number, lng: number) => Promise<string | null>;
